import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { format } from '../utils/dateUtils';
import googleCalendarService from '../services/googleCalendar';
import { fetchReports } from '../services/reportsService';

function Reports() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  // Initialize and check authentication
  useEffect(() => {
    const initGoogleCalendar = async () => {
      try {
        await googleCalendarService.initialize();
        const hasToken = !!googleCalendarService.getAccessToken();
        setIsSignedIn(hasToken);
        
        if (hasToken) {
          const profile = googleCalendarService.getProfile();
          setUserProfile(profile);
        }
      } catch (err) {
        console.error('Error initializing Google Calendar:', err);
      }
    };

    initGoogleCalendar();
  }, []);

  // Fetch reports on component mount
  useEffect(() => {
    const loadReports = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const authToken = localStorage.getItem('authToken');
        
        if (!authToken) {
          setError('Authentication required');
          setIsLoading(false);
          return;
        }

        // Fetch reports from API
        const userEmail = localStorage.getItem('userEmail');
        const reportsData = await fetchReports(authToken, userEmail);
        setReports(reportsData);
        
        // Select first report by default
        if (reportsData.length > 0) {
          setSelectedReport(reportsData[0]);
        }
      } catch (err) {
        console.error('Error fetching reports:', err);
        setError(err.message || 'Failed to fetch reports. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    // Fetch reports immediately when component mounts (if authToken exists)
    loadReports();
  }, []);

  const handleSignOut = async () => {
    try {
      await googleCalendarService.signOut();
      setIsSignedIn(false);
      setUserProfile(null);
      setReports([]);
      navigate('/');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return format(date, 'MMMM d, yyyy');
    } catch (err) {
      return timestamp;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#020617' }}>
      {/* Header */}
      <Box
        sx={{
          maxWidth: '100%',
          height: 64,
          bgcolor: 'rgba(15, 23, 42, 0.5)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          px: { xs: 2, sm: 3 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          zIndex: 20,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                background: 'linear-gradient(to top right, #8B5CF6 0%, #06B6D4 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                boxShadow: '0 10px 15px -3px rgba(139, 92, 246, 0.2)',
              }}
            >
              E
            </Box>
            <Box sx={{ 
              fontWeight: 'bold', 
              fontSize: '1.25rem', 
              color: 'white', 
              letterSpacing: '-0.05em',
              display: { xs: 'none', sm: 'block' },
            }}>
              EVA
            </Box>
          </Box>

          {/* Navigation Items */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
            {['CALENDAR', 'REPORTS', 'TASKS', 'SETTINGS'].map((item) => (
              <Box
                key={item}
                onClick={() => {
                  if (item === 'CALENDAR') {
                    navigate('/dashboard');
                  }
                }}
                sx={{
                  px: 2,
                  py: 1,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  color: item === 'REPORTS' ? 'white' : '#94A3B8',
                  bgcolor: item === 'REPORTS' ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  borderRadius: '0.5rem',
                  boxShadow: item === 'REPORTS' ? 'inset 0 2px 4px 0 rgba(255, 255, 255, 0.05)' : 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  letterSpacing: '0.15em',
                  '&:hover': {
                    color: 'white',
                    bgcolor: item === 'REPORTS' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                {item}
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Divider Line */}
          <Box sx={{ width: '1px', height: '24px', bgcolor: 'rgba(255, 255, 255, 0.1)', mx: 1 }} />
          
          {isSignedIn && userProfile ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, pl: 2 }}>
              <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                <Box sx={{ 
                  color: 'white', 
                  fontWeight: 500, 
                  fontSize: '0.875rem',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                }}>
                  {userProfile.name || userProfile.email}
                </Box>
                <Box 
                  onClick={handleSignOut}
                  sx={{ 
                    color: '#94A3B8',
                    fontSize: '0.625rem', 
                    cursor: 'pointer', 
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    '&:hover': { color: 'white' } 
                  }}
                >
                  Sign Out
                </Box>
              </Box>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: 'linear-gradient(to bottom right, #6366F1 0%, #06B6D4 100%)',
                  border: '2px solid #1E293B',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  cursor: 'pointer',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {/* Reports Content */}
        <Box
          sx={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '300px 1fr' },
            overflow: 'hidden',
            position: 'relative',
            minWidth: { md: '400px' },
          }}
        >
        {/* Background Ambient Glow */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '25%',
              left: 0,
              width: '600px',
              height: '600px',
              bgcolor: 'rgba(139, 92, 246, 0.05)',
              borderRadius: '50%',
              filter: 'blur(120px)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: '600px',
              height: '600px',
              bgcolor: 'rgba(6, 182, 212, 0.05)',
              borderRadius: '50%',
              filter: 'blur(100px)',
            }}
          />
        </Box>

        {/* Dossier Sidebar */}
        <Box
          sx={{
            borderRight: '1px solid rgba(255, 255, 255, 0.05)',
            bgcolor: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            zIndex: 10,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              borderTop: '1px solid rgba(255, 255, 255, 0.05)',
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
            }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} sx={{ color: '#5B7FFF' }} />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ m: 2, bgcolor: '#1E2442', color: '#FF6B6B' }}>
                {error}
              </Alert>
            ) : reports.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center', color: '#64748B', fontSize: '0.875rem' }}>
                <Typography variant="body2">No dossiers available</Typography>
              </Box>
            ) : (
              reports.map((report, index) => (
                <Box
                  key={index}
                  onClick={() => setSelectedReport(report)}
                  sx={{
                    p: 1.5,
                    borderRadius: '0.5rem',
                    bgcolor: selectedReport === report ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                    },
                    position: 'relative',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '30px',
                      background: 'linear-gradient(to right, transparent, #0f172a)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '0.8125rem',
                      fontWeight: selectedReport === report ? 600 : 500,
                      color: selectedReport === report ? 'white' : '#CBD5E1',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: 1.4,
                      '& p': {
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                      '& strong': {
                        fontWeight: selectedReport === report ? 700 : 600,
                      },
                    }}
                  >
                    <ReactMarkdown>{report.report_title || 'Untitled Dossier'}</ReactMarkdown>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>

        {/* Report Content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            zIndex: 10,
            bgcolor: 'rgba(2, 6, 23, 0.3)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: { xs: 3, sm: 4, md: 6 },
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
            }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress size={40} sx={{ color: '#5B7FFF' }} />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ bgcolor: '#1E2442', color: '#FF6B6B' }}>
                {error}
              </Alert>
            ) : !selectedReport ? (
              <Box sx={{ textAlign: 'center', color: '#94A3B8', mt: 8 }}>
                <Typography variant="h6">Select a report to view</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  color: '#CBD5E1',
                  '& h1': {
                    fontSize: '2rem',
                    fontWeight: 700,
                    color: 'white',
                    mb: 2,
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                  },
                  '& h2': {
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    color: 'white',
                    mt: 4,
                    mb: 2,
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                  },
                  '& h3': {
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: 'white',
                    mt: 3,
                    mb: 1.5,
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                  },
                  '& p': {
                    fontSize: '1rem',
                    lineHeight: 1.75,
                    mb: 2,
                    color: '#CBD5E1',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                  },
                  '& ul, & ol': {
                    mb: 2,
                    pl: 3,
                    '& li': {
                      mb: 1,
                      color: '#CBD5E1',
                      lineHeight: 1.75,
                    },
                  },
                  '& blockquote': {
                    borderLeft: '4px solid #5B7FFF',
                    pl: 2,
                    ml: 0,
                    fontStyle: 'italic',
                    color: '#94A3B8',
                    mb: 2,
                  },
                  '& strong': {
                    color: '#E2E8F0', // lighter shade for better readability
                    fontWeight: 600,
                  },
                  '& code': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    px: 1,
                    py: 0.5,
                    borderRadius: '0.25rem',
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                  },
                }}
              >
                <Box
                  sx={{
                    mb: 2,
                    '& h1, & h2, & p, & strong': {
                      fontSize: { xs: '1.75rem', sm: '2rem' },
                      fontWeight: 700,
                      color: 'white',
                      margin: 0,
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      lineHeight: 1.2,
                    },
                  }}
                >
                  <ReactMarkdown>{selectedReport.report_title || '# Untitled Report'}</ReactMarkdown>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#94A3B8',
                    mb: 4,
                    fontSize: '0.875rem',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                  }}
                >
                  <strong>Date:</strong> {formatDate(selectedReport.created_at)} | <strong>Attendees:</strong> {selectedReport.attendees || 'N/A'}
                </Typography>
                <ReactMarkdown
                  components={{
                    a: ({ node, children, ...props }) => (
                      <a
                        {...props}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#60A5FA', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        {children}
                      </a>
                    ),
                  }}
                >
                  {selectedReport.report_body || ''}
                </ReactMarkdown>
              </Box>
            )}
          </Box>
        </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default Reports;

